import {
  getIsBundleBbJsw,
  getIsBundleDevops,
  ProductKey
} from '@atlassiansox/bxpkit-core';
import _ from 'lodash';
import {
  GetEditionLabelsOptionsType,
  GetEditionLabelsReturnType,
  GetFeaturesListOptionsType,
  GetSideInfoHeaderOptionsType,
  GetTrialDurationSiteLabelOptionsType
} from '../../../../../model';
import {
  BITBUCKET,
  BUNDLE_LABEL_KEY,
  DEVELOPER_MODE,
  DEVOPS,
  FIRST_IMPRESSION_JIRA_SOFTWARE_FEATURE_LIST,
  FREE_EDITION_LABEL_KEY,
  JIRA_CORE,
  JIRA_PRODUCT_DISCOVERY,
  JSD,
  JSW,
  OPSGENIE,
  FABRIC,
  BEACON,
  SIGNUP_OFFERING_LABEL_KEY_PREFIX,
  SIGNUP_SIDE_INFO_HEADER_PREFIX,
  STATUSPAGE,
  TRIAL_DURATION_LABEL_KEY_EXISTING_SITE,
  TRIAL_DURATION_LABEL_KEY_NEW_SITE,
  WAC_SIGNUP_FORM_KEY_BASE,
  JIRA_FAMILY
} from './constants';
import { getProductNameFromKey } from './data-product';
import { useSignUpFormConfig } from '../../../../../hook';

/**
 * Label for side-info header (if applicable)
 */

export const getSideInfoHeader = ({
  labels,
  productKeys,
  developerMode
}: GetSideInfoHeaderOptionsType): string => {
  let contentKey = SIGNUP_SIDE_INFO_HEADER_PREFIX;

  if (getIsBundleBbJsw(productKeys)) {
    contentKey += BUNDLE_LABEL_KEY + JSW + BITBUCKET;
  }

  if (getIsBundleDevops(productKeys)) {
    contentKey += BUNDLE_LABEL_KEY + DEVOPS;
  }

  if (developerMode) {
    contentKey += BUNDLE_LABEL_KEY + DEVELOPER_MODE;
  }

  return labels[contentKey];
};

/**
 * Edition Labels, eg. 'Cloud Free', 'Cloud Premium'
 */

export const getEditionLabels = ({
  productKey,
  edition: originalEdition
}: GetEditionLabelsOptionsType): GetEditionLabelsReturnType => {
  let editionLabel = `Cloud ${_.capitalize(
    originalEdition || FREE_EDITION_LABEL_KEY
  )}`;
  let featuresListEditionLabelKey = originalEdition || FREE_EDITION_LABEL_KEY;

  if (getProductNameFromKey(productKey) === STATUSPAGE) {
    editionLabel = '';
  }

  return { editionLabel, featuresListEditionLabelKey };
};

/**
 * Trial duration text, eg. '1-month free cloud trial',
 * for existing sites
 *
 * Returns empty string for BB & Statuspage
 */

export const getTrialDurationExistingSiteLabel = ({
  productKey,
  labels
}: GetTrialDurationSiteLabelOptionsType): string => {
  const productName = getProductNameFromKey(productKey);
  return productName === BITBUCKET || productName === STATUSPAGE
    ? ''
    : labels[TRIAL_DURATION_LABEL_KEY_EXISTING_SITE];
};

/**
 * Trial duration text, eg. '7-day free cloud trial',
 * for new sites
 *
 * Returns empty string for BB & Statuspage
 */

export const getTrialDurationNewSiteLabel = ({
  productKey,
  labels
}: GetTrialDurationSiteLabelOptionsType): string => {
  const productName = getProductNameFromKey(productKey);
  return productName === BITBUCKET || productName === STATUSPAGE
    ? ''
    : labels[
        TRIAL_DURATION_LABEL_KEY_NEW_SITE +
          (productName === OPSGENIE ? OPSGENIE : '')
      ];
};

/**
 * Get list of features, eg. side-info <li/>'s
 */

export const getFeaturesList = ({
  labels,
  products,
  productKey,
  featuresListEditionLabelKey,
  migrationData,
  developerMode
}: GetFeaturesListOptionsType): string => {
  const { bundle } = useSignUpFormConfig();
  const isBundle = products.length > 1;
  const edition = _.capitalize(featuresListEditionLabelKey);
  const includesJiraServiceManagement = _.includes(
    _.map(products, (val) => val.product === ProductKey.JIRA_SERVICE_DESK),
    true
  );
  if (migrationData && migrationData.migrationSourceUuid) {
    // bitbucket
    if (bundle === 'bitbucket' || productKey.includes(ProductKey.BITBUCKET)) {
      // Forcing premium edition for Bitbucket CMTs.
      // The Edition is overriden to free when calling this function from the SideInfo component
      return labels[SIGNUP_OFFERING_LABEL_KEY_PREFIX + 'Premium' + BITBUCKET];
    }

    const migrationFeatureList =
      labels[`${WAC_SIGNUP_FORM_KEY_BASE}migrationFeatureList`] || '';
    if (
      migrationData.migrationUserLimit &&
      migrationData.migrationUserType &&
      migrationData.migrationProductName
    ) {
      return migrationFeatureList
        .replace('{productName}', migrationData.migrationProductName)
        .replace('{usageLimit}', migrationData.migrationUserLimit.toString())
        .replace('{userType}', migrationData.migrationUserType);
    }
    return migrationFeatureList;
  }

  let productNameFromKey = getProductNameFromKey(productKey);

  // JSD Standalone
  if (productKey.includes(ProductKey.JIRA_SERVICE_DESK)) {
    productNameFromKey = JSD;
  }

  if (
    productKey.includes(ProductKey.JIRA_CORE) &&
    products[0].edition === 'premium'
  ) {
    return labels[SIGNUP_OFFERING_LABEL_KEY_PREFIX + 'Premium' + JIRA_CORE];
  }

  // Jira Core Standalone
  if (productKey.includes(ProductKey.JIRA_CORE)) {
    productNameFromKey = JIRA_CORE;
  }

  // Jira Software Standalone
  if (productKey.includes(ProductKey.JIRA_SOFTWARE)) {
    productNameFromKey = FIRST_IMPRESSION_JIRA_SOFTWARE_FEATURE_LIST;
  }

  // Free bundles that include JSD
  if (
    isBundle &&
    includesJiraServiceManagement &&
    edition === FREE_EDITION_LABEL_KEY
  ) {
    productNameFromKey = BUNDLE_LABEL_KEY + JSD;
  }

  // jira-software-bitbucket
  if (getIsBundleBbJsw(products)) {
    productNameFromKey = BUNDLE_LABEL_KEY + JSW + BITBUCKET;
  }

  // devops
  if (getIsBundleDevops(products)) {
    productNameFromKey = BUNDLE_LABEL_KEY + DEVOPS;
  }

  if (developerMode) {
    productNameFromKey = BUNDLE_LABEL_KEY + DEVELOPER_MODE;
  }

  // team central
  if (productKey.includes(ProductKey.TEAM_CENTRAL)) {
    productNameFromKey = 'TeamCentral';
  }

  if (productKey.includes(ProductKey.JIRA_PRODUCT_DISCOVERY)) {
    productNameFromKey = JIRA_PRODUCT_DISCOVERY;
  }

  if (productNameFromKey.endsWith('DeveloperMode')) {
    return labels[
      SIGNUP_OFFERING_LABEL_KEY_PREFIX + 'Standard' + productNameFromKey
    ];
  }

  // canvas
  if (productKey.includes(ProductKey.FABRIC)) {
    productNameFromKey = FABRIC;
  }

  // beacon
  if (productKey.includes(ProductKey.BEACON)) {
    productNameFromKey = BEACON;
  }
  // jira family
  if (isBundle && bundle === 'jira') {
    productNameFromKey = JIRA_FAMILY;
  }

  return labels[
    SIGNUP_OFFERING_LABEL_KEY_PREFIX + edition + productNameFromKey
  ];
};
